import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Heart, DollarSign, Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESET_AMOUNTS = [5, 10, 20, 50, 100];

/**
 * <TipButton /> — Sends a $5-$100 tip from the fan's wallet credits to
 * an artist (or the platform when `artistId` is undefined). Enforces
 * the 48-hour new-buyer hold gate server-side; the UI shows the exact
 * error toast if the hold is still active.
 *
 * Props:
 *   token     — auth session token
 *   artistId  — artist_id string, OR omit for a platform tip
 *   artistName — display name of the recipient
 *   onSent()  — optional callback fired after a successful tip
 *   compact   — render a small icon-only button (for feed cards)
 */
const TipButton = ({ token, artistId, artistName, onSent, compact = false, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(10);
  const [customValue, setCustomValue] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const raw = customValue ? parseFloat(customValue) : amount;
    if (!raw || raw < 5 || raw > 100) {
      return toast.error("Tips must be between $5 and $100");
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/tips/send`,
        {
          amount_cents: Math.round(raw * 100),
          artist_id: artistId || undefined,
          to_platform: !artistId,
          note: (note || "").slice(0, 240),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`💌 Sent $${raw.toFixed(2)} tip to ${artistName || "StardomFans"} — thank you!`);
      setOpen(false);
      setCustomValue("");
      setNote("");
      if (onSent) onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Tip failed — check your wallet balance");
    } finally {
      setBusy(false);
    }
  };

  if (!token) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={
          compact
            ? `inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#EC008C]/15 border border-[#EC008C]/30 text-[#EC008C] text-xs font-bold hover:bg-[#EC008C]/25 ${className}`
            : `inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#EC008C] to-[#F0C464] text-black text-sm font-bold hover:brightness-110 shadow-lg shadow-[#EC008C]/25 ${className}`
        }
        data-testid={`tip-button${artistId ? `-${artistId}` : "-platform"}`}
        aria-label={`Send a tip to ${artistName || "StardomFans"}`}
      >
        <Heart className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} fill="currentColor" />
        {compact ? "Tip" : `Tip ${artistName || "us"}`}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4"
          onClick={() => !busy && setOpen(false)}
          data-testid="tip-modal"
        >
          <div
            className="bg-[#0A0E18] border border-[#EC008C]/40 rounded-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Heart className="w-4 h-4 text-[#EC008C]" fill="currentColor" />
                  <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">Wallet tip</p>
                </div>
                <h3 className="font-['Unbounded'] text-xl font-bold text-white">
                  Tip {artistName || "StardomFans"}
                </h3>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-[#8A92A0] hover:text-white"
                aria-label="Close"
                data-testid="tip-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-[#8A92A0] mb-4">
              Instantly moves Tokens from your wallet. $5 minimum · $100 maximum · 10/hour.
            </p>

            <div className="grid grid-cols-5 gap-2 mb-4" data-testid="tip-presets">
              {PRESET_AMOUNTS.map((v) => (
                <button
                  key={v}
                  onClick={() => { setAmount(v); setCustomValue(""); }}
                  className={`h-10 rounded-lg font-bold text-sm transition-colors ${
                    (customValue ? "" : String(amount)) === String(v)
                      ? "bg-[#EC008C] text-black"
                      : "bg-[#050810] border border-[#1A2030] text-white hover:border-[#EC008C]/40"
                  }`}
                  data-testid={`tip-preset-${v}`}
                >
                  ${v}
                </button>
              ))}
            </div>

            <div className="mb-4">
              <label className="text-xs text-[#8A92A0]">Custom amount (USD)</label>
              <div className="relative mt-1">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A92A0]" />
                <input
                  type="number"
                  min="5"
                  max="100"
                  step="0.01"
                  value={customValue}
                  onChange={(e) => setCustomValue(e.target.value)}
                  placeholder="e.g. 15"
                  className="w-full pl-9 pr-3 py-2 bg-black/40 border border-[#1A2030] rounded-lg text-white text-sm"
                  data-testid="tip-custom-input"
                />
              </div>
            </div>

            <label className="block mb-4">
              <span className="text-xs text-[#8A92A0]">Message (optional, 240 chars)</span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 240))}
                rows={2}
                className="w-full mt-1 bg-black/40 border border-[#1A2030] rounded-lg px-3 py-2 text-white text-sm resize-none"
                placeholder="Loved your last album!"
                data-testid="tip-note-input"
              />
            </label>

            <button
              onClick={send}
              disabled={busy}
              className="w-full h-11 rounded-full bg-gradient-to-r from-[#EC008C] to-[#F0C464] text-black font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="tip-send-btn"
            >
              {busy ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
              ) : (
                <><Heart className="w-4 h-4" fill="currentColor" /> Send ${customValue || amount} tip</>
              )}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default TipButton;
